<template>
	<ui-root :part="{ type: 'interface'}" :data="{}" :errs="errors">
		<div class="likeInput comment-form">
			<div :class="bottomLineClass">
				<api-error v-for="e in errors" :level="e.level" :message="e.message"></api-error>
				<div class="comment-body">
					<html-area-part ref="htmlArea" :part="partDefinition" :data="form" />
					<div class="comment-buttons">
						<file-add icon="paper-clip" class="link-like-fi" :read-only="saving" v-model="form.attachments"/>
						<sym-icon name="send" :class="sendButtonClass" @click.prevent="addComment"/>
					</div>
				</div>
			</div>
			<div v-if="form.srcType===5" @click="focusEditor" >
				<label>
					<input type="checkbox" v-model="form.forCustomer" @click.stop>
					<span style="margin-left: 8px;">{{ env.msg("comment.forCustomer") }}</span>
				</label>
			</div>
			<div>
				<div v-for="attach in form.attachments" class="comment-attachment" @click="focusEditor">
					<span>{{attach.name}}</span>
					<span class="size">{{formatFileSize(attach.size)}}</span>
					<sym-icon v-if="!saving" name="close" class="link-like-fi" @click.stop="removeAttach(attach)"/>
				</div>
			</div>
		</div>
	</ui-root>
</template>

<script setup lang="ts">
import {ref, reactive, computed} from 'vue';
import {Comment} from "@t/note/Comment.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import HtmlAreaPart from '@/fd/editors/html/HtmlAreaPart.vue';
import SymIcon from "@fd/helpers/SymIcon.vue";
import ApiError from "@/fd/helpers/ApiError.vue";
import UiRoot from "@fd/containers/UiRoot.vue";
import FileAdd from "@/vue/file/FileAdd.vue";
import {formatFileSize} from "@/utils/FormatUtil.ts";
import type {AttFile} from "@t/file/AttFile.ts";
import {ErrorMap} from "@fd/PartInjectKeys.ts";

	
const props = defineProps<{
	comment: Comment,
	name: String,
	readOnly: Boolean
}>();

defineExpose({replyComment});

const env = injectEnv();
const bus = connectEventBus("CommentForm");
const form = ref<Comment>({ ...props.comment });
const errors = reactive<ErrorMap>([]);
const emit = defineEmits(['created']);
const htmlArea = ref(null);
const saving = ref<boolean>(false);

const bottomLineClass = computed(() => {
	return !!form.value?.attachments?.length ? "bottomLine" : "";
});

const sendButtonClass = computed(() => {
	return saving.value ? "disabled-click" : "link-like-fi";
});

const partDefinition = computed(() => {
		return {
			property: "content",
			name: props.name,
			/*readOnly: saving.value,*/
			displayStyle: "box",
			options: {  style: "max-height:300px;" },
			catch: "content"
		};
	});

function addComment(): void {
	//console.log("send comment:"+JSON.stringify(form.value));
	if (saving.value) return;
	saving.value = true;
	errors.length = 0;
	let endpoint = "api/comments";
	env.net.fetch(endpoint, "POST",form.value)
		.then(response => {
			if (response.ok) {
				return response.json();
			}
			else {
				response.json().then(errorData => {
					console.log("Problems:"+JSON.stringify(errorData));
					errorData.problems.forEach(item => errors.push(item));
				});
				return null;
			}
		})
		.then(data => {
			if (data) {
				emit("created", data);
				resetComment();
			}
		})
		.catch((error) => {
			console.log("Error saving comment:"+error);
			errors.push({level:"SEVERE",message: error});
		})
		.finally(() => {
			saving.value = false;
		});
}

function replyComment(comment: Comment): void {
	bus.fire("flush", props.name);
	console.log("content:"+form.value.content);
	form.value.content=(form.value.content && form.value.content!=="<p></p>"?form.value.content:"")+"<blockquote>"+comment.content+"</blockquote><br>";
	bus.fire("update", props.name);
	focusEditor();
}

function focusEditor(): void {
	bus.fire("focus", props.name);
}

function resetComment(): void {
	form.value.content='';
	form.value.forCustomer=false;
	form.value.attachments = [];
	bus.fire("update", props.name);
}

function removeAttach(attach: AttFile): void {
	form.value.attachments = form.value.attachments.filter(item => item.name !== attach.name);
}

//if (props.edit) console.log("Form comment:"+JSON.stringify(form.value));
</script>

<style>
.comment-form {
	display:flex;
	flex-direction: column;
	height: unset !important;
	margin-right: 12px !important;
	margin-left: 2px !important;
}

.comment-form:focus-within {
	outline: none;
    box-shadow: 0 0 2px 2px var(--outline-color);
}

.comment-buttons {
	display:flex;
	flex-direction: row;
	gap: 4px;
	align-items: baseline;
	padding-bottom: 2px;
}

.comment-body {
	display:flex;
	flex-direction: row;
	gap: 4px;
	align-items: flex-end;
}

.comment-body .ProseMirror > p:last-child {
    padding-bottom: 0;
	margin-block-end: unset;
}

.comment-attachment {
	display:flex;
	align-items: center;
	flex-direction: row;
	gap: 16px;
}

.comment-attachment .size {
	opacity: 0.5;
}

</style>
